import React, { Fragment, useEffect } from 'react';
import { Menu } from 'antd';
import {
  HomeFilled,
  LineChartOutlined,
  DatabaseOutlined,
  PlaySquareFilled,
  ScheduleOutlined,
  UserOutlined,
  UsergroupAddOutlined,
  CrownOutlined,
  CloudUploadOutlined,
  CloudDownloadOutlined,
  CloudServerOutlined,
  DeploymentUnitOutlined,
  GroupOutlined,
  MailOutlined,
  FileExcelOutlined,
  AppstoreAddOutlined,
  SisternodeOutlined,
  VideoCameraAddOutlined,
  VideoCameraOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  ReadOutlined,
  FilePdfOutlined,
  ExperimentOutlined,
  ApartmentOutlined,
  ShareAltOutlined,
  IdcardOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { hidesMemberStatus, hidesTeamStatus } from '../../../../utils/answerStatusView';

// ------------------------------------------------------------------
// 上部メニューの定義（2026-09〜10 上部メニュー整理：名称の統一・メニュー内の整理・第1階層の名前とアイコン）
// 出典: 事業企画「04_上部メニュー一覧_変更案v1.2.xlsx」の「整理v2_再度採番」シート（2026-10-14。コメントの番号は同シートの番号）。
//   第1階層の名前・アイコンは「04_上部メニュー一覧_現状 10月1日午後.xlsx」のまま。
//   表の「★」（特権管理者だけのメニュー）は名前の先頭にそのまま付ける（2026-10-19 依頼側の希望）。
//   「◯（サーベイ対象のみ）」は needs で指定（企業管理者・シーズン管理者に、該当する診断があるときだけ出す）。
//
// ・メニューは 1 つの定義（MENU）だけを持ち、項目ごとに表示するロールを roles で指定する。
//   ロール別のメニューはここから自動で作る（同じ画面はどのロールでも同じ名前・同じ場所＝表記ルール R6）。
// ・構成は「カテゴリ（第1階層）▾ → グループ ▸ → 画面」。ホーム・Myページはカテゴリではなく直接のリンク。
//   カテゴリにカーソルを合わせるとドロップダウン、▸ のグループに合わせると右に開く。
//   改善案の「A＞B」は「グループ A ▸ 画面 B」を表す。
// ・表示できる画面が 1 つも無いグループ・カテゴリは出さない。
// ・roles はルート（App.js の Auth 許可ロール）と揃える。
//
// ・画面が 1 つだけのグループ（企業管理者の エンゲージメント 等）も ▸ で開く形にする
//   （今後メニューが増える可能性があるため。2026-09-30 確認済み）
const R9 = 9; // 特権管理者
const R2 = 2; // リ・カレント管理者
const R3 = 3; // 企業管理者
const R7 = 7; // シーズン管理者（企業管理者 シーズン指定）
const R1 = 1; // 受講者（role 0/4/5/6/8 も受講者メニュー）
const ALL = [R9, R2, R3, R7, R1];

// 「◯（サーベイ対象のみ）」の条件（企業管理者・シーズン管理者だけに効く。needs で指定）。
//   codes＝サーバー /api/users/auth の surveyCodes（企業管理者＝自社グループ、シーズン管理者＝担当シーズンの診断コード）
const NEEDS = {
  any: (codes) => codes.length > 0, // サーベイ（シーズン）が 1 つでもある
  team: (codes) => codes.some((c) => ['40010', '60020'].includes(c)), // 360度診断・フォロワーシップ（チーム回答状況で選べる診断）
  e3: (codes) => codes.some((c) => ['30010', '90301'].includes(c)), // エンゲージメント
};
const NEEDS_ROLES = [R3, R7];

const MENU = [
  { key: 'home', href: '/', label: 'ホーム', icon: <HomeFilled />, roles: ALL }, // 1
  // ホーム と同じく直接のリンク（旧「マイ・サーベイ ▸ マイサーベイ」）
  { key: 'mySruvey', href: '/mysurvey', label: 'Myページ', icon: <ScheduleOutlined />, roles: ALL }, // 2
  // トライアルユーザーだけの動画（ロール固有の画面）は getItems1 で追加する
  {
    key: 'usermgrmenu',
    label: 'メンバー', // 3（シーズン管理者には出さない：表示できる画面が無い）
    icon: <UserOutlined />,
    children: [
      {
        key: 'usermgr_org',
        label: '組織・メンバー設定', // 3-a
        children: [
          { key: 'grouplist', href: '/grouplist', label: 'グループ', icon: <UsergroupAddOutlined />, roles: [R9] }, // 3-a-1
          { key: 'orgmgr', href: '/orgmgr', label: '組織・所属', icon: <ApartmentOutlined />, roles: [R9, R2, R3] }, // 3-a-2
          { key: 'exceluserupload', href: '/exceluserupload', label: 'メンバー登録（Excel）', icon: <FileExcelOutlined />, roles: [R9, R2, R3] }, // 3-a-3
          { key: 'csvuserupload', href: '/csvuserupload', label: 'メンバー登録（CSV）', icon: <CloudUploadOutlined />, roles: [R9] }, // 3-a-4
          { key: 'e3depmgr', href: '/e3depmgr', label: '閲覧範囲', icon: <ApartmentOutlined />, roles: [R9, R2, R3] }, // 3-a-5
        ],
      },
      {
        key: 'usermgr_ticket',
        label: '★チケット発行・情報更新', // 3-b
        children: [
          { key: 'csvusercheck', href: '/csvusercheck', label: 'アカウント登録確認（CSV）', icon: <CloudUploadOutlined />, roles: [R9] }, // 3-b-1
          { key: 'csvrelationupload', href: '/csvrelationupload', label: '評価関係登録（CSV）', icon: <CloudUploadOutlined />, roles: [R9] }, // 3-b-2
          { key: 'csvtokeninsert', href: '/csvtokeninsert', label: 'チケット発行（CSV）', icon: <CloudUploadOutlined />, roles: [R9] }, // 3-b-3
          { key: 'csvuserupdate', href: '/csvuserupdate', label: 'メンバー情報更新（履歴あり）', icon: <CloudUploadOutlined />, roles: [R9] }, // 3-b-4
          { key: 'csvuserupdateonly', href: '/csvuserupdateonly', label: 'メンバー情報更新（履歴なし）', icon: <CloudUploadOutlined />, roles: [R9] }, // 3-b-5
        ],
      },
      {
        key: 'usermgr_rc',
        label: '★リ・カレント管理', // 3-c
        children: [
          // アイコンは付けない（2026-10-01 決定のまま）
          { key: 'usermgr', href: '/usermgr', label: 'リ・カレント管理者', roles: [R9] }, // 3-c-1
          { key: 'trialusermgr', href: '/trialusermgr', label: 'トライアルメンバー', icon: <ExperimentOutlined />, roles: [R9] }, // 3-c-2
          { key: 'instructormgr', href: '/instructormgr', label: 'インストラクター', icon: <IdcardOutlined />, roles: [R9] }, // 3-c-3
        ],
      },
    ],
  },
  {
    key: 'surveydownloadmenu',
    label: '結果管理', // 4
    icon: <LineChartOutlined />,
    children: [
      // 4-a メンバー回答状況：特権・RC と 企業管理者・シーズン管理者 で画面が違う（同じ名前で出す）
      { key: 'respondentmgr', href: '/respondentmgr', label: 'メンバー回答状況', icon: <CrownOutlined />, roles: [R9, R2] },
      // answerStatus: 回答状況の画面。シーズン管理者の回答状況指定で見られないときは出さない（utils/answerStatusView.js・2026-10）
      //   member = エンゲージメント・その他の両方なしで出さない／team = 360度系なしで出さない
      { key: 'corpmgr', href: '/corpmgr', label: 'メンバー回答状況', icon: <UsergroupAddOutlined />, roles: [R3, R7], needs: 'any', answerStatus: 'member' },
      { key: 'status360', href: '/status360', label: 'チーム回答状況', icon: <DeploymentUnitOutlined />, roles: [R9, R2, R3, R7], needs: 'team', answerStatus: 'team' }, // 4-b
      { key: 'pdfbatch', href: '/pdfbatch', label: '個人結果一括ダウンロード', icon: <CloudDownloadOutlined />, roles: [R9, R2, R3, R7] }, // 4-c
      { key: 'csvdownload', href: '/csvdownload', label: '回答データ（Excel）', icon: <CloudDownloadOutlined />, roles: [R9, R2] }, // 4-d
      {
        key: 'result_multi',
        label: '多面評価・診断', // 4-e
        children: [
          { key: 'download40010_SS', href: '/download40010_SS', label: '組織結果（360度診断）', icon: <CloudDownloadOutlined />, roles: [R9, R2] }, // 4-e-1
          { key: 'exceldownload40010', href: '/exceldownload40010', label: '個人結果一覧（360度診断）', icon: <FileExcelOutlined />, roles: [R9, R2] }, // 4-e-2
          { key: 'download60020_SS', href: '/download60020_SS', label: '組織結果（フォロワーシップ）', icon: <CloudDownloadOutlined />, roles: [R9, R2] }, // 4-e-3
          { key: 'exceldownload60020', href: '/exceldownload60020', label: '個人結果一覧（フォロワーシップ）', icon: <FileExcelOutlined />, roles: [R9, R2] }, // 4-e-4
        ],
      },
      {
        key: 'result_social',
        label: 'ソーシャルスタイル', // 4-f
        children: [
          { key: 'exceldownload50040', href: '/exceldownload50040', label: '個人結果一覧', icon: <FileExcelOutlined />, roles: [R9, R2] }, // 4-f-1
        ],
      },
      {
        key: 'result_ep',
        label: '人事評価力', // 4-g
        children: [
          { key: 'download10010_company', href: '/download10010_company', label: '組織結果', icon: <CloudDownloadOutlined />, roles: [R9, R2] }, // 4-g-1
          { key: 'exceldownload10010', href: '/exceldownload10010', label: '個人結果一覧', icon: <FileExcelOutlined />, roles: [R9, R2] }, // 4-g-2
          {
            key: 'result_ep_external',
            label: '外部データ', // 4-g-3
            children: [
              { key: 'epCSVUpload', href: '/csvupload', label: '回答情報登録', icon: <CloudUploadOutlined />, roles: [R9, R2] }, // 4-g-3-1
              { key: 'epMakeReport', href: '/makereport', label: '個人・組織結果', icon: <FilePdfOutlined />, roles: [R9, R2] }, // 4-g-3-2
            ],
          },
        ],
      },
      {
        key: 'result_e3',
        label: 'エンゲージメント', // 4-h
        children: [
          // 4-h-1 組織結果（Web 画面。PDF もこの画面から出す）。
          //   2026-10-26 本格リリースで、暫定の「Web は特権だけ／ほかは従来の PDF 画面（/download30010）」を 1 つに戻した。
          //   従来の PDF 画面はメニューから外し、特権管理者だけが URL で開ける（App.js）
          { key: 'e3orgresult', href: '/results/30010/org', label: '組織結果', icon: <CloudDownloadOutlined />, roles: [R9, R2, R3, R7], needs: 'e3' },
          { key: 'exceldownload30010', href: '/exceldownload30010', label: '組織結果（Excel）', icon: <FileExcelOutlined />, roles: [R9, R2] }, // 4-h-2
          { key: 'excelcomparedownload30010', href: '/excelcomparedownload30010', label: '経年比較（Excel）', icon: <FileExcelOutlined />, roles: [R9, R2] }, // 4-h-3
          { key: 'companysum30010', href: '/companysum30010', label: '組織平均集計処理', icon: <CloudServerOutlined />, roles: [R9, R2] }, // 4-h-4
        ],
      },
      {
        key: 'result_personality',
        label: 'パーソナリティ', // 4-i
        children: [
          { key: 'exceldownload50020', href: '/exceldownload50020', label: '個人結果一覧', icon: <FileExcelOutlined />, roles: [R9, R2] }, // 4-i-1
          { key: 'companysum50020', href: '/companysum50020', label: '組織平均集計処理', icon: <CloudServerOutlined />, roles: [R9, R2] }, // 4-i-2
        ],
      },
      {
        key: 'result_character',
        label: 'キャラクター', // 4-j
        children: [
          { key: 'exceldownload50030', href: '/exceldownload50030', label: '個人結果一覧', icon: <FileExcelOutlined />, roles: [R9, R2] }, // 4-j-1
          // 4-j-2 組織平均集計処理（/companysum50030）はメニューから外した（キャラクター診断には不要な画面とされた・2026-10-14）。
          //   ルートは残している。直して使える画面かは後で調べる
        ],
      },
      {
        key: 'result_women',
        label: '女性活躍', // 4-k
        children: [
          { key: 'download70010', href: '/download70010', label: '組織結果', icon: <CloudDownloadOutlined />, roles: [R9, R2] }, // 4-k-1
        ],
      },
      {
        key: 'result_diversity',
        label: 'ダイバーシティ', // 4-l
        children: [
          { key: 'download80010', href: '/download80010', label: '組織結果', icon: <CloudDownloadOutlined />, roles: [R9, R2] }, // 4-l-1
        ],
      },
    ],
  },
  {
    key: 'datamgrmenu',
    label: 'データ設定', // 5
    icon: <DatabaseOutlined />,
    children: [
      { key: 'publicdocmgr', href: '/publicdocmgr', label: '外部ファイル', icon: <ShareAltOutlined />, roles: [R9, R2] }, // 5-a
      { key: 'seasonlist', href: '/seasonlist', label: '★サーベイシーズン', icon: <SisternodeOutlined />, roles: [R9] }, // 5-b
      {
        key: 'data_survey',
        label: '★サーベイ管理', // 5-c
        children: [
          { key: 'questionsetmgr', href: '/questionsetmgr', label: '設問セット', icon: <AppstoreAddOutlined />, roles: [R9] }, // 5-c-2
          { key: 'surveyguidemgr', href: '/surveyguidemgr', label: '回答案内', icon: <FileTextOutlined />, roles: [R9] }, // 5-c-3
          { key: 'trialcampaignlist', href: '/trialcampaignlist', label: 'トライアルキャンペーン', icon: <ExperimentOutlined />, roles: [R9] }, // 5-c-4
        ],
      },
      { key: 'auditLog', href: '/auditlog', label: '★操作ログ', icon: <HistoryOutlined />, roles: [R9] }, // 5-d
    ],
  },
  {
    key: 'sendmailmenu',
    label: 'メール', // 6
    icon: <MailOutlined />,
    children: [
      { key: 'mailtemplate', href: '/mailtemplate', label: 'テンプレート管理', icon: <GroupOutlined />, roles: [R9, R2] }, // 6-a
      { key: 'mailreservatelist', href: '/mailreservatelist', label: '★配信一覧', icon: <MailOutlined />, roles: [R9] }, // 6-b
      {
        key: 'mail_reserve',
        label: '★配信予約', // 6-c
        children: [
          { key: 'mailsendreservate', href: '/mailsendreservate', label: '個別予約', icon: <MailOutlined />, roles: [R9] }, // 6-c-1
          { key: 'multimailsendreservate', href: '/multimailsendreservate', label: '複数予約', icon: <MailOutlined />, roles: [R9] }, // 6-c-2
          { key: 'csvmailsendreservate', href: '/csvmailsendreservate', label: 'CSV予約', icon: <FileExcelOutlined />, roles: [R9] }, // 6-c-3
        ],
      },
      {
        key: 'mail_send',
        label: '★即時送信', // 6-d
        children: [
          { key: 'surveymailsend', href: '/surveymailsend', label: '個別送信', icon: <MailOutlined />, roles: [R9] }, // 6-d-1
          { key: 'csvmailsend', href: '/csvmailsend', label: 'CSV送信', icon: <MailOutlined />, roles: [R9] }, // 6-d-2
        ],
      },
    ],
  },
  {
    key: 'videomenu',
    label: '動画＆記事',
    // 記事の項目が無いロール（RC）は従来どおり「動画」と表示する（RC の記事は右端の「記事」リンク）
    labelIfOnly: { video_setup: '動画' },
    icon: <PlaySquareFilled />,
    children: [
      {
        key: 'video_setup',
        label: '動画設定',
        children: [
          { key: 'videoupload', href: '/videoupload', label: '動画登録', icon: <VideoCameraAddOutlined />, roles: [R9, R2] }, // No.73
          { key: 'videomgr', href: '/videomgr', label: '動画情報管理', icon: <VideoCameraOutlined />, roles: [R9, R2] }, // No.74
          { key: 'videodeliverymgr', href: '/videodeliverymgr', label: '動画納品管理', icon: <PlayCircleOutlined />, roles: [R9, R2] }, // No.75
        ],
      },
      {
        key: 'article_setup',
        label: '★記事設定',
        children: [
          { key: 'articleseries', href: '/articleseries', label: '記事シリーズ', icon: <ReadOutlined />, roles: [R9] }, // No.76
          { key: 'articleupload', href: '/articleupload', label: '記事登録', icon: <ReadOutlined />, roles: [R9] }, // No.77
        ],
      },
    ],
  },
  // 記事一覧（ロール固有の画面・名称改善の対象外）
  { key: 'articlemenu', href: '/article', label: '記事', icon: <PlaySquareFilled />, roles: [R2] },
];

// MENU からロールの項目だけを残し、antd Menu の items 形式にする（画面の無いグループは消す）。
// codes（診断コードの一覧）を渡すと、needs の付いた項目は条件に合うときだけ残す（企業管理者・シーズン管理者のみ。
// codes が null＝取得できなかったときは条件を見ずに出す）
// hiddenStatus（{ member, team }。シーズン管理者の回答状況指定で見られない画面）の answerStatus の項目は出さない
const buildItems = (nodes, role, codes = null, hiddenStatus = {}) =>
  nodes
    .map((n) => {
      if (Array.isArray(n.children)) {
        const children = buildItems(n.children, role, codes, hiddenStatus);
        if (!children.length) return null;
        // 残った子が 1 グループだけのときの表示名（例: 動画＆記事 → 動画）
        const only = children.length === 1 && n.labelIfOnly ? n.labelIfOnly[children[0].key] : null;
        return { key: n.key, label: only || n.label, icon: n.icon, children };
      }
      if (!n.roles.includes(role)) return null;
      if (n.answerStatus && hiddenStatus[n.answerStatus]) return null;
      if (n.needs && NEEDS_ROLES.includes(role) && Array.isArray(codes) && !NEEDS[n.needs](codes)) return null;
      return { key: n.key, label: <a href={n.href}>{n.label}</a>, icon: n.icon };
    })
    .filter(Boolean);

// 特権管理者・リ・カレント管理者はロールだけで決まる。企業管理者・シーズン管理者は診断コードで出し分けるため、表示のたびに作る
const ITEMS_BY_ROLE = {
  [R9]: buildItems(MENU, R9),
  [R2]: buildItems(MENU, R2),
};

// 受講者メニュー（トライアルユーザーの場合は動画メニューを追加）
const getItems1 = (isTrialUser) => {
  const baseItems = buildItems(MENU, R1);
  if (isTrialUser) {
    baseItems.push({
      label: <a href="/trialvideoview">動画</a>,
      key: 'trialvideomenu',
      icon: <PlaySquareFilled />, // 動画＆記事 と同じアイコン
    });
  }
  return baseItems;
};

// メニュー項目セットを再帰的に走査し、リーフ項目（label が <a href> の要素）の
// { key, href } 一覧を収集する。グループ・サブメニューの親（label が文字列）は href を持たない。
const collectLeafHrefs = (items, acc = []) => {
  if (!Array.isArray(items)) return acc;
  items.forEach((item) => {
    if (!item) return;
    if (Array.isArray(item.children)) {
      collectLeafHrefs(item.children, acc);
    }
    const label = item.label;
    // label が React 要素（<a href="...">）で href を持つものだけがリーフ
    if (
      label &&
      typeof label === 'object' &&
      label.props &&
      typeof label.props.href === 'string'
    ) {
      acc.push({ key: item.key, href: label.props.href });
    }
  });
  return acc;
};

// 現在のパス名に一致するリーフの key を返す。
// マッチ規則 = 完全一致を優先 → 無ければ最長 prefix 一致（/path/... 等の下位・パラメータ経路も親メニューを点灯）。
// "/"（Home）は pathname === "/" の時のみ（prefix で全経路にマッチするのを防ぐ）。一致無しは null。
const findSelectedKey = (items, pathname) => {
  const leaves = collectLeafHrefs(items);
  // 完全一致
  const exact = leaves.find((l) => l.href === pathname);
  if (exact) return exact.key;
  // 最長 prefix 一致（"/" は除外）
  let best = null;
  leaves.forEach((l) => {
    if (l.href === '/') return; // Home は完全一致のみ
    if (pathname.startsWith(l.href + '/')) {
      if (!best || l.href.length > best.href.length) best = l;
    }
  });
  return best ? best.key : null;
};

function LeftMenu(props) {
  // console.log({ props });
  let user = useSelector((state) => state.user);
  const location = useLocation();

  if (!user.userData) return null;
  const { role } = user.userData;
  // console.log(user.userData);

  // 現在見ているページのメニュー項目に active 色（Ph.6-6 の theme.components.Menu の
  // selected スタイル = orange-active 文字 + orange-background wash）を点灯させる。
  const renderMenu = (items) => {
    const selectedKey = findSelectedKey(items, location.pathname);
    return (
      <Menu
        mode={props.mode}
        items={items}
        selectedKeys={selectedKey ? [selectedKey] : []}
      />
    );
  };

  if (ITEMS_BY_ROLE[role]) {
    // 特権管理者・リ・カレント管理者
    return renderMenu(ITEMS_BY_ROLE[role]);
  }
  if (NEEDS_ROLES.includes(role)) {
    // 企業管理者・シーズン管理者（「◯（サーベイ対象のみ）」の項目は診断コードで出し分ける）
    const codes = Array.isArray(user.userData.surveyCodes) ? user.userData.surveyCodes : null;
    const hiddenStatus = { member: hidesMemberStatus(user.userData), team: hidesTeamStatus(user.userData) };
    return renderMenu(buildItems(MENU, role, codes, hiddenStatus));
  }
  //受講者メニュー（トライアルユーザーの場合は動画メニュー追加）
  //role 4/5/6は廃止。未移行ユーザーがいた場合は受講者メニューにフォールバック
  const isTrialUser = !!user.userData.trialCampaignKey;
  return renderMenu(getItems1(isTrialUser));
}

export default LeftMenu;
